/** Assistant panel UI state: open/closed, the current thread, a draft to prefill. */
import { useSyncExternalStore } from 'react';

interface State {
  /** Open on laptop and phone (slide-over / bottom sheet). */
  open: boolean;
  /** Docked on ultrawide (a preference, remembered in this browser). */
  docked: boolean;
  view: 'chat' | 'threads';
  threadId: string | null;
  /** Text to put in the composer when the panel opens ("Ask about this"). */
  draft: string | null;
  /** Bumped to focus the composer. */
  focusTick: number;
}

function readDocked(): boolean {
  try {
    return localStorage.getItem('nova.assistant.docked') !== 'false';
  } catch {
    return true;
  }
}

let state: State = { open: false, docked: typeof window !== 'undefined' ? readDocked() : true, view: 'chat', threadId: null, draft: null, focusTick: 0 };
const listeners = new Set<() => void>();

function set(patch: Partial<State>): void {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
}

export function useAssistantState(): State {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => state,
    () => state,
  );
}

export function setDocked(docked: boolean): void {
  try {
    localStorage.setItem('nova.assistant.docked', String(docked));
  } catch {
    /* private mode */
  }
  set({ docked });
}

/** Open the panel (docking it on ultrawide) and optionally prefill the composer. */
export function openAssistant(opts: { prompt?: string; ultrawide?: boolean } = {}): void {
  if (opts.ultrawide) setDocked(true);
  set({ open: true, view: 'chat', draft: opts.prompt ?? null, focusTick: state.focusTick + 1 });
}

export function closeAssistant(ultrawide: boolean): void {
  if (ultrawide) setDocked(false);
  set({ open: false });
}

export function toggleAssistant(ultrawide: boolean): void {
  const visible = ultrawide ? state.docked : state.open;
  if (visible) closeAssistant(ultrawide);
  else openAssistant({ ultrawide });
}

export function setThread(threadId: string | null): void {
  set({ threadId, view: 'chat' });
}

export function setView(view: State['view']): void {
  set({ view });
}
